import * as Yup from "yup";
import React, { useState } from "react";
import formatCnpj from "@brazilian-utils/format-cnpj";
import formatCpf from "@brazilian-utils/format-cpf";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";
import { Link, useHistory } from "react-router-dom";
import { MdClose, MdOutlinePermContactCalendar } from "react-icons/md";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import type { Method } from "axios";

import ContainedButton from "components/layout/form/containedButton";
import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import theme from "styles/theme";
import { cpfCnpjCursorAlign, validateCNPJ, validateCPF } from "utils/functions";
import {
  registerLocation,
  registerValues,
  toggleStep,
} from "store/actions/first-access-mi-actions";
import { registerValuesMembership } from "store/actions/membership-actions";

import axios from "axios";
import {
  Container,
  ContainerButtons,
  FormContainer,
  HeaderContainer,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalStyle,
  TextBody,
  TextFooter,
} from "./styles";

const Document = (): JSX.Element => {
  const [isOpenModal, setIsOpenModal] = useState(false);

  const { document, access_token } = useSelector(
    // @ts-ignore
    (state) => state.firstAccessReducer
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const validationSchema = Yup.object().shape({
    document: Yup.string()
      .test({
        name: "validate-length",
        message: "Insira um tamanho válido para o documento",
        exclusive: true,
        test: (value) => {
          const numbers = (value || "").replace(/[^\d]/g, "");

          if (
            (numbers.length >= 0 && numbers.length < 11) ||
            (numbers.length >= 12 && numbers.length < 14)
          ) {
            return false;
          }

          return true;
        },
      })
      .test({
        name: "validate-cnpj",
        message: "Insira um CNPJ válido",
        exclusive: true,
        test: (value) => {
          const numbers = (value || "").replace(/[^\d]/g, "");

          if (!validateCNPJ(numbers) && numbers.length === 14) {
            return false;
          }

          return true;
        },
      })
      .test({
        name: "validate-cpf",
        message: "Insira um CPF válido",
        exclusive: true,
        test: (value) => {
          const numbers = (value || "").replace(/[^\d]/g, "");

          if (!validateCPF(numbers) && numbers.length === 11) {
            return false;
          }

          return true;
        },
      })
      .required("É necessário informar um documento"),
  });

  const formik = useFormik({
    initialValues: {
      document: document || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await generateAccessToken(values);
    },
  });

  const generateAccessToken = async (values): Promise<void> => {
    const req = axios.create({
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Accept-Language": "pt-BR",
        client_id: process.env.REACT_APP_CLIENT_ID,
      },
      baseURL: process.env.REACT_APP_API_URL_OAUTH,
    });

    const headers = {
      "Content-Type": "application/json",
      "Accept-Language": "pt-BR",
      Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
    };
    const parameters = {
      method: "POST" as Method,
      headers,
    };
    const body = {
      grant_type: "client_credentials",
    };

    try {
      const result = await req.post("oauth/access-token", body, parameters);
      await handleSubmit(values, result.data.access_token);
      localStorage.setItem(
        "@UltragazEmpresarial:accessToken",
        result.data.access_token
      );
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;

        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const handleSubmit = async (values, access_token): Promise<void> => {
    const login = values.document.replace(/[^\d]/g, "");
    const params = {
      login,
      "is-active": true,
      _limit: 1,
      _offset: 0,
    };

    const headers = {
      access_token,
    };

    try {
      const result = await api.get(`industrial/customer/v1/users`, {
        headers,
        params,
      });

      if (result.data.length === 0) {
        await requestGetCustomer(values, access_token);
      } else {
        setIsOpenModal(true);
      }
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;

        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const requestGetCustomer = async (values, access_token): Promise<void> => {
    const document = values.document.replace(/[^\d]/g, "");
    const params = {
      document,
      _limit: 1,
      _offset: 0,
    };
    let nameCompletedSerasa = "";

    try {
      const { data } = await api.get(`industrial/customer/v1/customers`, {
        headers: {
          access_token,
        },
        params,
      });

      dispatch(
        registerValues({
          siteId: data[0]?.customerAddresses[0]?.siteId,
        })
      );

      if (data && data.length === 0) {
        let documentNumber = values.document.replace(/[^\d]/g, "");
        let filialDocument = "";
        let digitoDocument = "";

        if (documentNumber.length > 11) {
          const [numbers, filialDigito] = values.document.split("/");
          const [filial, digito] = filialDigito.split("-");

          documentNumber = numbers.replace(/[^\d]/g, "").replace(/0+$/, "");
          filialDocument = filial;
          digitoDocument = digito;
        }

        if (process.env.REACT_APP_ENV === "build-prod") {
          api
            .get("Serasa/v1/document", {
              headers: {
                access_token,
              },
              params: {
                documento: documentNumber,
                filial: filialDocument,
                digito: digitoDocument,
              },
            })
            .then(({ data: dataSerasa }) => {
              if (dataSerasa?.nomeOuRazaoSocial) {
                nameCompletedSerasa = dataSerasa?.nomeOuRazaoSocial;
              }
            })
            .finally(() => {
              dispatch(
                registerValuesMembership({ document, nameCompletedSerasa })
              );
              dispatch(registerValues({ document }));
              history.push("/adesao");
            });
        } else {
          dispatch(registerValuesMembership({ document, nameCompletedSerasa }));
          dispatch(registerValues({ document }));
          history.push("/adesao");
        }
      } else {
        const { document, customerId, customerCode } = data[0];
        dispatch(registerValues({ document, customerId, customerCode }));

        if (data[0]?.id) {
          dispatch(registerValuesMembership({ awsCustomerId: data[0].id }));

          // activating digital way
          if (data[0]?.customerAddresses.length <= 2) {
            dispatch(
              registerValues({
                unitId: data[0]?.customerAddresses[0]?.unitId,
              })
            );
          }
        }

        await requestPostUser(access_token);
      }
    } catch (err) {
      const erro = err.response || false;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status === 502) {
          toast.error(
            document.length > 11
              ? "Informe um CNPJ válido"
              : "Informe um CPF válido"
          );
          return;
        }
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const requestPostUser = async (access_token): Promise<void> => {
    const body = {
      document: formik.values.document.replace(/[^\d]/g, ""),
    };

    const headers = {
      access_token,
    };

    try {
      const result = await api.post("industrial/customer/v1/users", body, {
        headers,
      });

      const { location } = result.headers;

      dispatch(registerLocation(location));
      requestGetEmails(location);
      requestGetPhoneNumber(location);
    } catch (err) {
      const erro = err.response || false;

      if (erro) {
        const { message } = err.response.data;

        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const requestGetEmails = async (location: string): Promise<void> => {
    const params = {
      contactType: "EMAIL",
      _limit: 99,
    };
    const headers = {
      access_token,
    };

    try {
      const { data } = await api.get(`${location}/contacts`, {
        headers,
        params,
      });

      dispatch(registerValues({ emails: data }));
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;

        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }

      dispatch(registerValues({ backPage: 1 }));
      dispatch(toggleStep(2.1));
    }
  };

  const requestGetPhoneNumber = async (location): Promise<void> => {
    const params = {
      contactType: "PHONE",
      _limit: 99,
    };
    const headers = {
      access_token,
    };

    try {
      const { data } = await api.get(`${location}/contacts`, {
        headers,
        params,
      });

      dispatch(registerValues({ phones: data }));
      dispatch(toggleStep(2));
    } catch (err) {
      const erro = err.response;

      if (erro) {
        const { message } = err.response.data;

        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }

      dispatch(registerValues({ backPage: 1 }));
      dispatch(toggleStep(2.1));
    }
  };

  const handleDocument = (event: React.ChangeEvent<any>): void => {
    const cursorStart = event.target.selectionStart;
    const cursorEnd = event.target.selectionEnd;
    const numbers = event.target.value.replace(/[^\d]/g, "");
    event.target.value = event.target.value.replace(/[^\d]/g, "");

    if (numbers.length > 0 && numbers.length < 12) {
      event.target.value = formatCpf(numbers);
    }

    if (numbers.length > 11) {
      event.target.value = formatCnpj(numbers);
    }

    cpfCnpjCursorAlign(event, cursorStart, cursorEnd);
    formik.handleChange(event);
  };

  return (
    <>
      <Container>
        <HeaderContainer>
          <h1>Vamos começar!</h1>
          <h2>Informe seu CPF ou CNPJ de cadastro para continuar</h2>
        </HeaderContainer>
        <FormContainer onSubmit={formik.handleSubmit}>
          <TextField
            name="document"
            inputMode="numeric"
            placeholder="Insira seu CPF ou CNPJ *"
            label="Insira seu CPF ou CNPJ *"
            value={formik.values.document}
            LeadingIcon={MdOutlinePermContactCalendar}
            propsLeadingIcon={{
              color: theme.colors.blue,
            }}
            onChange={handleDocument}
            onBlur={formik.handleBlur}
            errorCondition={!!formik.errors.document}
            errorMessage={`${formik.errors.document}`}
          />
          <ContainerButtons>
            <ContainedButton
              block
              type="submit"
              continue
              loading={formik.isSubmitting}
              width="100%"
              TrailingIcon={IoMdArrowForward}
              propsTrailingIcon={{
                size: 20,
              }}
              disabled={
                !!formik.errors.document || !formik.values.document.length
              }
            >
              Continuar
            </ContainedButton>
            <Link to="/">
              <OutlinedButton
                width="100%"
                LeadingIcon={IoMdArrowBack}
                propsLeadingIcon={{
                  size: 20,
                }}
                onClick={() => dispatch(toggleStep(1))}
                color={theme.colors.purple}
              >
                Voltar
              </OutlinedButton>
            </Link>
          </ContainerButtons>
        </FormContainer>
      </Container>
      <ModalStyle
        show={isOpenModal}
        onHide={() => setIsOpenModal(false)}
        centered
      >
        <ModalContainer>
          <ModalHeader>
            <span>Usuário já cadastrado no sistema</span>

            <MdClose size={24} onClick={() => setIsOpenModal(false)} />
          </ModalHeader>
          <ModalBody>
            <img width={46} src="/static/alert.svg" alt="Alerta" />
            <TextBody>
              Não foi possível realizar o Primeiro Acesso, o documento digitado
              já está em uso.
            </TextBody>
          </ModalBody>
          <ModalFooter>
            <TextFooter>Como deseja prosseguir?</TextFooter>

            <ContainerButtons>
              <Link to="/esqueci-senha-mi">
                <ContainedButton
                  block
                  width="100%"
                  TrailingIcon={IoMdArrowForward}
                  propsTrailingIcon={{
                    size: 20,
                  }}
                >
                  Recuperar Senha
                </ContainedButton>
              </Link>

              <Link to="/">
                <ContainedButton
                  block
                  width="100%"
                  TrailingIcon={IoMdArrowForward}
                  propsTrailingIcon={{
                    size: 20,
                  }}
                >
                  Acessar o Portal
                </ContainedButton>
              </Link>

              <Link to="/acessar?link=adicionar-unidade">
                <ContainedButton
                  block
                  width="100%"
                  TrailingIcon={IoMdArrowForward}
                  propsTrailingIcon={{
                    size: 20,
                  }}
                >
                  Adicionar Nova Unidade
                </ContainedButton>
              </Link>
            </ContainerButtons>
          </ModalFooter>
        </ModalContainer>
      </ModalStyle>
    </>
  );
};

export default Document;
